export type FloatingSide = 'top' | 'bottom' | 'left' | 'right';
export type FloatingAlign = 'start' | 'center' | 'end';

export interface ComputeFloatingPositionArgs {
  triggerRect: DOMRect;
  contentWidth: number;
  contentHeight: number;
  side: FloatingSide;
  align: FloatingAlign;
  /** Gap between trigger and content in pixels (before rem scaling in CSS — use px here). */
  offsetPx?: number;
  /** When true, automatically flip to the opposite side if content would be clipped. Defaults to true. */
  flip?: boolean;
}

function crossAlign(triggerSpan: number, contentSpan: number, align: FloatingAlign): number {
  if (align === 'start') return 0;
  if (align === 'end') return triggerSpan - contentSpan;
  return (triggerSpan - contentSpan) / 2;
}

function oppositeSide(s: FloatingSide): FloatingSide {
  const map: Record<FloatingSide, FloatingSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };
  return map[s];
}

function calcPosition(
  triggerRect: DOMRect,
  contentWidth: number,
  contentHeight: number,
  side: FloatingSide,
  align: FloatingAlign,
  offsetPx: number,
): { top: number; left: number } {
  let top = 0;
  let left = 0;

  switch (side) {
    case 'bottom':
      top = triggerRect.bottom + offsetPx;
      left = triggerRect.left + crossAlign(triggerRect.width, contentWidth, align);
      break;
    case 'top':
      top = triggerRect.top - contentHeight - offsetPx;
      left = triggerRect.left + crossAlign(triggerRect.width, contentWidth, align);
      break;
    case 'right':
      left = triggerRect.right + offsetPx;
      top = triggerRect.top + crossAlign(triggerRect.height, contentHeight, align);
      break;
    case 'left':
      left = triggerRect.left - contentWidth - offsetPx;
      top = triggerRect.top + crossAlign(triggerRect.height, contentHeight, align);
      break;
    default:
      break;
  }

  return { top, left };
}

function wouldClip(
  side: FloatingSide,
  coords: { top: number; left: number },
  contentWidth: number,
  contentHeight: number,
  pad: number,
): boolean {
  if (typeof window === 'undefined') return false;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  switch (side) {
    case 'bottom':
      return coords.top + contentHeight + pad > vh;
    case 'top':
      return coords.top < pad;
    case 'right':
      return coords.left + contentWidth + pad > vw;
    case 'left':
      return coords.left < pad;
    default:
      return false;
  }
}

/**
 * Returns fixed `top` / `left` for a floating layer anchored to a trigger rect.
 * Auto-flips to the opposite side when content would be clipped by the viewport.
 */
export function computeFloatingPosition({
  triggerRect,
  contentWidth,
  contentHeight,
  side,
  align,
  offsetPx = 8,
  flip = true,
}: ComputeFloatingPositionArgs): { top: number; left: number } {
  let coords = calcPosition(triggerRect, contentWidth, contentHeight, side, align, offsetPx);

  if (flip && wouldClip(side, coords, contentWidth, contentHeight, offsetPx)) {
    const flipped = oppositeSide(side);
    const flippedCoords = calcPosition(
      triggerRect,
      contentWidth,
      contentHeight,
      flipped,
      align,
      offsetPx,
    );
    if (!wouldClip(flipped, flippedCoords, contentWidth, contentHeight, offsetPx)) {
      coords = flippedCoords;
    }
  }

  if (typeof window !== 'undefined') {
    const pad = offsetPx;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    coords.left = Math.min(Math.max(coords.left, pad), Math.max(pad, vw - contentWidth - pad));
    coords.top = Math.min(Math.max(coords.top, pad), Math.max(pad, vh - contentHeight - pad));
  }

  return coords;
}
